'use client';

import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/auth-context';
import { Button } from '@/components/ui/button';
import { ButtonSpinner } from '@/components/ui/button-spinner';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { trackCtaClicked } from '@/lib/posthog';

interface PricingCTAButtonProps {
  planId?: string;
}

/**
 * Client component for pricing CTA button that handles authentication and navigation.
 *
 * This component handles the interactive "Get Started" button logic:
 * - Checks if user is authenticated
 * - Navigates to login if not authenticated
 * - Navigates to checkout if authenticated
 * - Shows loading state during navigation
 *
 * @param planId - Plan identifier for checkout (default: "monthly")
 *
 * @example
 * ```tsx
 * <PricingCTAButton planId="monthly" />
 * ```
 *
 * @see {@link PricingCTASection} - Full CTA section component
 */
export function PricingCTAButton({ planId = 'monthly' }: PricingCTAButtonProps) {
  const { user, loading } = useAuth();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const handleGetStarted = async () => {
    trackCtaClicked({
      location: 'pricing_card',
      destination: user ? `/checkout?plan=${planId}` : '/login',
      page: 'pricing',
      label: 'Start 14-Day Free Trial',
    });

    // For paid plan, navigate to custom checkout page
    if (!user) {
      router.push('/login');
      return;
    }

    setIsLoading(true);
    // Navigate to custom checkout page
    router.push(`/checkout?plan=${planId}`);
  };

  return (
    <Button
      onClick={handleGetStarted}
      disabled={isLoading || loading}
      className="w-full btn-gradient-purple"
      size="lg"
    >
      {isLoading || loading ? (
        <>
          <ButtonSpinner />
          Processing...
        </>
      ) : (
        <>
          Start 14-Day Free Trial
          <ArrowRight className="h-4 w-4 ml-2" />
        </>
      )}
    </Button>
  );
}
